import React, { useEffect, useMemo } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Statistic,
  Tag,
  Typography,
} from 'antd'
import { Controller, DefaultValues, useForm } from 'react-hook-form'
import { useUnit } from 'effector-react'
import { WalletGuardWallet } from '../../types'
import { formatWalletDate, WALLET_CURRENCIES, WALLET_NETWORKS } from '../config'
import {
  $createdWallet,
  $createWalletFeedback,
  $finalWallets,
  $finalWalletsHasError,
  $nextCheckAt,
  $paymentSystems,
  checkTimerFinished,
  createWalletFx,
  createWalletValidationFailed,
  feedbackClosed,
  finalWalletsRequested,
  formMounted,
  getFinalWalletsFx,
  riskCancelled,
  riskConfirmed,
  walletNameFromAddress,
  walletSubmitted,
} from './model'
import { CreateWalletValues } from './types'

const defaultValues: DefaultValues<CreateWalletValues> = {
  address: '',
  name: '',
  type: 'SENDER',
  currency: 'USDT',
  network: undefined,
  paymentSystems: [],
  comment: '',
}

const RiskDetails = ({ wallet }: { wallet: WalletGuardWallet }) => (
  <Flex vertical gap={8}>
    <Typography.Text>
      BitOK оценил адрес как высокорисковый. Всё равно добавить кошелёк?
    </Typography.Text>
    <Typography.Text>
      AML score: <Typography.Text strong>{wallet.amlScore}</Typography.Text>
    </Typography.Text>
    {wallet.riskFlags.length > 0 && (
      <Flex gap={4} wrap="wrap">
        {wallet.riskFlags.map((flag) => (
          <Tag key={flag.code} color="error">
            {flag.label || flag.code}
          </Tag>
        ))}
      </Flex>
    )}
    <Typography.Text type="secondary">
      Проверено: {formatWalletDate(wallet.lastCheckedAt)}
    </Typography.Text>
  </Flex>
)

export const CreateWalletForm = () => {
  const { control, handleSubmit, watch, setValue, setError, reset } =
    useForm<CreateWalletValues>({
      mode: 'onChange',
      defaultValues,
    })
  const type = watch('type')
  const currency = watch('currency')
  const network = watch('network')
  const address = watch('address')?.trim()
  const {
    submitWallet,
    pending,
    feedback,
    closeFeedback,
    createdWallet,
    nextCheckAt,
    finishCheckTimer,
    mountForm,
    confirmRisk,
    cancelRisk,
    finalWallets,
    loadingFinalWallets,
    finalWalletsHasError,
    requestFinalWallets,
    paymentSystems,
  } = useUnit({
    submitWallet: walletSubmitted,
    pending: createWalletFx.pending,
    feedback: $createWalletFeedback,
    closeFeedback: feedbackClosed,
    createdWallet: $createdWallet,
    nextCheckAt: $nextCheckAt,
    finishCheckTimer: checkTimerFinished,
    mountForm: formMounted,
    confirmRisk: riskConfirmed,
    cancelRisk: riskCancelled,
    finalWallets: $finalWallets,
    loadingFinalWallets: getFinalWalletsFx.pending,
    finalWalletsHasError: $finalWalletsHasError,
    requestFinalWallets: finalWalletsRequested,
    paymentSystems: $paymentSystems,
  })

  // Пока адрес созданного кошелька проверяется, форма заблокирована:
  // иначе новый кошелёк вытеснит этот, и подтверждение его риска потеряется.
  const checking = createdWallet?.checkStatus === 'CHECKING'

  // Транзитный кошелёк можно привязать только к финальному с той же сетью и валютой.
  const compatibleFinalWallets = useMemo(
    () =>
      finalWallets.filter(
        (wallet) => wallet.currency === currency && wallet.network === network,
      ),
    [finalWallets, currency, network],
  )

  useEffect(() => {
    if (type === 'TRANSIT') requestFinalWallets()
  }, [type, requestFinalWallets])

  // Состояние формы живёт в react-hook-form, поэтому результат запроса
  // применяем здесь: после успеха очищаем форму, ошибки сервера ставим на поля.
  useEffect(() => {
    const unwatchCreated = createWalletFx.done.watch(() => reset(defaultValues))
    const unwatchValidation = createWalletValidationFailed.watch((items) =>
      items.forEach(({ field, msg }) =>
        setError(field as keyof CreateWalletValues, { message: msg }),
      ),
    )
    return () => {
      unwatchCreated()
      unwatchValidation()
    }
  }, [reset, setError])

  // Открыли страницу — начинаем с чистого листа: кошелёк и сообщение
  // из прошлого визита не показываем.
  useEffect(() => {
    mountForm()
  }, [mountForm])

  return (
    <Card>
      <Typography.Title
        level={5}
        id="create-wallet-title"
        style={{
          marginTop: 0,
          marginBottom: 20,
          fontSize: 14,
          letterSpacing: '0.06em',
        }}
      >
        Добавить кошелек
      </Typography.Title>
      <Form
        component={false}
        layout="vertical"
        size="large"
        variant="filled"
        disabled={pending || checking}
      >
        <form
          name="create-wallet"
          aria-labelledby="create-wallet-title"
          onSubmit={handleSubmit((values) => submitWallet(values))}
          noValidate
        >
          <Row gutter={16}>
            <Col xs={24} lg={16}>
              <Controller
                name="address"
                control={control}
                rules={{
                  required: 'Введите адрес кошелька',
                  pattern: {
                    value: /^[a-zA-Z0-9]+$/,
                    message:
                      'Адрес может содержать только латинские буквы и цифры',
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Адрес кошелька"
                    required
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Input
                      {...field}
                      onChange={(event) =>
                        field.onChange(event.target.value.trim())
                      }
                      placeholder="Введите адрес кошелька"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} lg={8}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Название / метка">
                    <Input
                      {...field}
                      placeholder={
                        address
                          ? walletNameFromAddress(address)
                          : 'Например: ПС Cryptomus'
                      }
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={16}>
              <Controller
                name="type"
                control={control}
                rules={{ required: 'Выберите тип кошелька' }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Тип"
                    required
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Select
                      {...field}
                      onChange={(value) => {
                        field.onChange(value)
                        setValue('finalWalletId', undefined)
                      }}
                      options={[
                        { value: 'SENDER', label: 'Кошелёк отправителя' },
                        { value: 'TRANSIT', label: 'Транзитный кошелёк' },
                        { value: 'FINAL', label: 'Финальный кошелёк' },
                        { value: 'OWN', label: 'Свой кошелёк' },
                      ]}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="paymentSystems"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Платёжные системы">
                    <Select
                      {...field}
                      mode="tags"
                      allowClear
                      placeholder="Добавить ПС…"
                      options={paymentSystems.map((value) => ({
                        value,
                        label: value,
                      }))}
                      tokenSeparators={[',']}
                      notFoundContent="Введите название платёжной системы"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="currency"
                control={control}
                rules={{ required: 'Выберите валюту' }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Валюта"
                    required
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Select
                      {...field}
                      onChange={(value) => {
                        field.onChange(value)
                        setValue('finalWalletId', undefined)
                      }}
                      options={WALLET_CURRENCIES.map((value) => ({
                        value,
                        label: value,
                      }))}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="network"
                control={control}
                rules={{ required: 'Выберите сеть' }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Сеть"
                    required
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Select
                      {...field}
                      onChange={(value) => {
                        field.onChange(value)
                        setValue('finalWalletId', undefined)
                      }}
                      placeholder="Выберите сеть"
                      options={WALLET_NETWORKS.map((value) => ({
                        value,
                        label: value,
                      }))}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            {type === 'TRANSIT' && (
              <Col span={24}>
                <Controller
                  name="finalWalletId"
                  control={control}
                  rules={{
                    required: 'Выберите финальный кошелёк для транзитного',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      label="Финальный кошелёк"
                      required
                      validateStatus={error ? 'error' : undefined}
                      help={error?.message}
                      extra={
                        finalWalletsHasError ? (
                          <Typography.Text type="danger">
                            Не удалось загрузить финальные кошельки.{' '}
                            <Button
                              type="link"
                              size="small"
                              onClick={() => requestFinalWallets()}
                            >
                              Повторить
                            </Button>
                          </Typography.Text>
                        ) : !network ? (
                          'Сначала выберите сеть.'
                        ) : !loadingFinalWallets &&
                          !compatibleFinalWallets.length ? (
                          `Нет финальных кошельков ${currency} в сети ${network}. Сначала создайте кошелёк с типом «Финальный».`
                        ) : undefined
                      }
                    >
                      <Select
                        {...field}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        loading={loadingFinalWallets}
                        disabled={
                          pending ||
                          loadingFinalWallets ||
                          finalWalletsHasError ||
                          !network
                        }
                        placeholder="Выберите финальный кошелёк"
                        notFoundContent={
                          loadingFinalWallets
                            ? 'Загрузка…'
                            : 'Финальные кошельки не найдены'
                        }
                        options={compatibleFinalWallets.map((wallet) => ({
                          value: wallet.id,
                          label: `${wallet.name} · ${wallet.currency} · ${wallet.address}`,
                        }))}
                      />
                    </Form.Item>
                  )}
                />
              </Col>
            )}
            <Col span={24}>
              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Комментарий">
                    <Input.TextArea
                      {...field}
                      placeholder="Комментарий к кошельку"
                      autoSize={{ minRows: 2, maxRows: 5 }}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
          {checking && (
            <Alert
              type="info"
              message="Кошелёк создан, идёт AML-проверка адреса"
              description={
                <Flex gap={8} align="center">
                  <Typography.Text>Обновим статус через</Typography.Text>
                  <Statistic.Timer
                    type="countdown"
                    value={nextCheckAt}
                    format="mm:ss"
                    valueStyle={{ fontSize: 14 }}
                    onFinish={() => finishCheckTimer()}
                  />
                </Flex>
              }
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {feedback && (
            <Alert
              type={feedback.type}
              message={feedback.message}
              showIcon
              closable
              afterClose={() => closeFeedback()}
              style={{ marginBottom: 16 }}
            />
          )}
          <Button type="primary" htmlType="submit" loading={pending}>
            Добавить кошелёк
          </Button>
        </form>
      </Form>
      <Modal
        open={createdWallet?.riskConfirmationRequired === true}
        title="Высокий риск адреса"
        okText="Добавить кошелёк"
        okButtonProps={{ danger: true }}
        cancelText="Отмена"
        confirmLoading={pending}
        onOk={() => confirmRisk()}
        onCancel={() => cancelRisk()}
      >
        {createdWallet && <RiskDetails wallet={createdWallet} />}
      </Modal>
    </Card>
  )
}
